import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { toast } from "sonner";
import { StepProgress } from "../components/plan/StepProgress";
import { LoadingScreen } from "../components/plan/LoadingScreen";
import { Step1Trip } from "../components/plan/Step1Trip";
import { Step2Desires } from "../components/plan/Step2Desires";
import { Step3Preferences } from "../components/plan/Step3Preferences";
import { INITIAL, toggle } from "../components/plan/planConstants";

const PENDING_JOB_KEY = "voyage_r_pending_job";

export default function PlanPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Ref pour éviter la double-exécution si les deps changent à step=4
  const generationStartedRef = useRef(false);
  // Ref pour annuler le polling si l'utilisateur quitte la page de génération
  const cancelledRef = useRef(false);

  const set = (key, val) => setData((d) => ({ ...d, [key]: val }));
  const tog = (key, val) => setData((d) => ({ ...d, [key]: toggle(d[key], val) }));
  const next = () => setStep((s) => s + 1);
  const prev = () => setStep((s) => s - 1);

  const handleGenerate = useCallback(async () => {
    setLoading(true);
    setError("");
    cancelledRef.current = false;
    try {
      let res;
      for (let attempt = 1; attempt <= 5; attempt++) {
        try {
          const response = await axios.post(`${API}/generate-trip`, {
            ...data,
            budget: data.budget_montant
              ? `${data.budget_montant} € ${data.budget_type === "par_personne" ? "par personne" : "total"}${data.vols_inclus ? " (vols déjà réservés)" : ""}`
              : "",
          });
          res = response.data;
          break;
        } catch (err) {
          const status = err.response?.status;
          if ((!status || status >= 500 || status === 408 || status === 429) && attempt < 5) {
            await new Promise((r) => setTimeout(r, 3000 * attempt));
            continue;
          }
          throw err;
        }
      }

      const jobId = res.job_id;
      if (!jobId) throw new Error("Pas de job_id reçu");

      // Mémorise le job en cours : le Dashboard pourra reprendre le suivi
      // même si le polling ci-dessous expire (génération longue sur mobile).
      localStorage.setItem(
        PENDING_JOB_KEY,
        JSON.stringify({ job_id: jobId, started_at: Date.now() })
      );

      // Fenêtre de polling : ~5 min (75 × 4 s) sur l'écran de génération.
      let attempts = 0;
      while (attempts < 75) {
        await new Promise((r) => setTimeout(r, 4000));
        attempts++;
        if (cancelledRef.current) return;

        let job;
        try {
          job = (await axios.get(`${API}/jobs/${jobId}`)).data;
        } catch (pollErr) {
          const pollStatus = pollErr.response?.status;
          // Erreurs réseau / passerelle / timeout transitoires (pas de réponse,
          // 5xx incluant 502/503/504/520/522/524, ou 408/429) → on continue de
          // poller : le job tourne toujours côté serveur.
          if (!pollStatus || pollStatus >= 500 || pollStatus === 408 || pollStatus === 429) continue;
          throw pollErr;
        }

        if (job.status === "complete" && job.trip_id) {
          localStorage.removeItem(PENDING_JOB_KEY);
          if (cancelledRef.current) return;
          toast.success("Votre voyage a été créé !");
          navigate(`/trip/${job.trip_id}`);
          return;
        }
        if (job.status === "error") {
          localStorage.removeItem(PENDING_JOB_KEY);
          throw new Error(job.error || "Erreur lors de la génération");
        }
      }

      // Toujours en cours après 5 min : on ne montre PAS d'erreur (le job
      // continue côté serveur et finira par réussir). On redirige vers le
      // tableau de bord qui reprendra le suivi — évite tout doublon.
      if (cancelledRef.current) return;
      toast.message(
        "Votre voyage prend un peu plus de temps que prévu — il apparaîtra dans votre tableau de bord dans un instant."
      );
      navigate("/dashboard");
      return;
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      const msg = typeof detail === "string" ? detail : "Erreur de génération. Veuillez réessayer.";
      if (cancelledRef.current) return;
      setError(msg);
      toast.error(msg);
      setStep(3);
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [data, navigate]);

  useEffect(() => {
    if (step !== 4) { generationStartedRef.current = false; return; }
    if (generationStartedRef.current) return;
    generationStartedRef.current = true;
    handleGenerate();
    return () => { cancelledRef.current = true; };
  }, [step, handleGenerate]);

  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <Navbar />
      <StepProgress step={step < 4 ? step : 4} total={4} />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 pb-16">
        {step === 1 && <Step1Trip data={data} set={set} onNext={next} />}
        {step === 2 && <Step2Desires data={data} set={set} tog={tog} onNext={next} onPrev={prev} />}
        {step === 3 && (
          <Step3Preferences
            data={data}
            set={set}
            tog={tog}
            error={error}
            onNext={() => { setError(""); setStep(4); }}
            onPrev={prev}
          />
        )}
        {step === 4 && <LoadingScreen />}
      </div>
    </div>
  );
}
